import { readStored, writeStored } from './storage';
import baseWords from './words.json';
import ukWords from './words.uk.json';
import usWords from './words.us.json';

export enum Region {
  UK = 'UK',
  US = 'US',
}

type WordsByLength = Record<string, string[]>;

const STORAGE_KEY = 'lingo:region';

/*
 * Locales that use US spelling. Everything else falls back to UK, which covers
 * en-GB plus the Commonwealth (en-AU, en-NZ, en-IE, en-IN, en-ZA...). Canada is
 * deliberately on the UK side: Canadian English keeps colour/centre/favourite.
 */
const US_LOCALES = ['en-us', 'en-ph'];

const toRegion = (value: string | null): Region | null => {
  const requested = value === null ? null : value.toUpperCase();

  return requested === Region.UK || requested === Region.US ? requested : null;
};

const readOverride = (): Region | null => {
  const fromQuery = toRegion(new URLSearchParams(window.location.search).get('region'));

  if (fromQuery !== null) {
    writeStored(STORAGE_KEY, fromQuery);

    return fromQuery;
  }

  return toRegion(readStored(STORAGE_KEY));
};

/* The picked region wins over detection, and keeps winning on every visit after. */
export const persistRegion = (region: Region): void => {
  writeStored(STORAGE_KEY, region);
};

export const resolveRegion = (): Region => {
  try {
    const override = readOverride();

    if (override !== null) {
      return override;
    }

    const locales = window.navigator.languages ?? [window.navigator.language];

    return locales.some((locale) => US_LOCALES.includes(locale.toLowerCase())) ? Region.US : Region.UK;
  } catch {
    return Region.UK;
  }
};

const merge = (addOn: WordsByLength): WordsByLength => {
  const base: WordsByLength = baseWords;
  const merged: WordsByLength = {};

  Object.keys(base).forEach((length) => {
    merged[length] = [...(base[length] ?? []), ...(addOn[length] ?? [])].sort();
  });

  return merged;
};

const wordsByRegion: Record<Region, WordsByLength> = {
  [Region.UK]: merge(ukWords),
  [Region.US]: merge(usWords),
};

export const getPossibleWords = (region: Region): WordsByLength => wordsByRegion[region];
